import {
  useParsedSearchParams,
  useDebouncedValue,
  MultiSelect,
  MultiSelectLoadOptions,
  Stack,
  Text,
} from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import {
  ComponentProps,
  useEffect,
  createContext,
  PropsWithChildren,
  useContext,
} from "react";
import { useRequiredParams } from "../../utils/url";
import { usePointsQuery, pointQueries } from "../point.queries";
import {
  useFetchBuildings,
  useEnergyReportQuery,
  useEnergyReportQueries,
  EnergyReportParams,
  PointEnergyReport,
} from "./point-energy.queries";
import {
  DateResolution,
  DataType,
  formatRange,
  energyResolutionMap,
  DateRange,
  getReportData,
  getComparisonQueryRanges,
} from "./point-energy.utils";
import { Level } from "../../level/level.model";
import { Point, PointWithBuilding, WithValue } from "../point.model";

type Option = ComponentProps<typeof MultiSelect.Options>["options"][number];

type PointEnergyReportStateContextType = {
  handleClearAll: () => void;
  handleBuildingSelect: (value: string) => void;
  handleDataTypeSelect: (value: string) => void;
  handlePointSelect: (selected: Set<string>) => void;
  handleChangeActiveTab: (value: string) => void;
  handleChangeComparisonYears: (years?: Set<string>) => void;
  onApply: (value: DateRange, resolutionValue: DateResolution) => void;
  comparisonReports: PointEnergyReport[][];
  selectedReports: PointEnergyReport[];
  comparisonYears: number[];
  selectedPoints: (Point & PointWithBuilding & WithValue)[];
  loadPointOptions: import("@fidelix/fx-miranda").MultiSelectLoadOptions;
  allBuildings: Level[] | undefined;
  setSearchParams: (
    updater: (params: URLSearchParams) => void,
    options?: { replace?: boolean },
  ) => void;
  dataType: number | undefined;
  buildingId: number | undefined;
  pointIds: number[];
  resolution: DateResolution;
  startDate: string;
  endDate: string;
  activeTab: string;
  search: string;
  defaultDateRange: { start: string; end: string };
  isLoading: boolean;
  placeholderText: string | undefined;
  showPoints: boolean;
  endYear: number;
};

const PointEnergyReportStateContext =
  createContext<PointEnergyReportStateContextType | null>(null);

export function PointEnergyReportStateProvider({
  children,
}: PropsWithChildren) {
  const defaultDateRange = {
    start: DateTime.local().startOf("year").toISODate(),
    end: DateTime.local().toISODate(),
  };

  const { parsedParams, setSearchParams, clearSearchParams } =
    useParsedSearchParams({
      active: { type: "string", defaultValue: "visual" },
      dataType: { type: "string", defaultValue: "" },
      buildingId: { type: "string", defaultValue: "" },
      pointIds: {
        parse: (p) => p.getAll("pointIds").map(Number),
      },
      search: { type: "string", defaultValue: "" },
      resolution: { type: "string", defaultValue: DateResolution.year },
      comparisonYears: {
        parse: (p) => p.getAll("comparisonYears").map(Number),
      },
      start: {
        type: "string",
        defaultValue: defaultDateRange.start,
      },
      end: {
        type: "string",
        defaultValue: defaultDateRange.end,
      },
      showPoints: { type: "string", defaultValue: "false" },
    });

  const debouncedPointSearchTerm = useDebouncedValue(parsedParams.search, 500);
  const queryClient = useQueryClient();
  const { companyId } = useRequiredParams({ companyId: "number" });

  const dataType = parsedParams.dataType ? +parsedParams.dataType : undefined;
  const buildingId = parsedParams.buildingId
    ? +parsedParams.buildingId
    : undefined;
  const pointIds = parsedParams.pointIds || [];

  // For normalized heating, we need to fetch regular heating data
  const actualDataType =
    dataType === DataType.HeatingNormalized ? DataType.Heating : dataType;

  const { data: allBuildings } = useFetchBuildings();

  const { data: points = [] } = usePointsQuery(
    {
      levelId: buildingId,
      dataType: actualDataType,
      withValues: false,
      maxResults: 20,
      search: debouncedPointSearchTerm,
    },
    {
      enabled: !!dataType,
    },
  );

  const selectedPoints = pointIds
    .map((id) => points?.find((point) => point.id === id))
    .filter((point): point is NonNullable<typeof point> => point !== undefined);

  const loadPointOptions: MultiSelectLoadOptions = async ({
    search,
  }): Promise<Option[]> => {
    if (!dataType || !buildingId) return [];

    try {
      const result = await queryClient.fetchQuery(
        pointQueries.list({
          search,
          companyId,
          levelId: buildingId,
          dataType: actualDataType,
          withValues: false,
          maxResults: 20,
        }),
      );

      return result.map((point) => ({
        value: point.id.toString(),
        label: point.name,
        content: (
          <Stack axis={"y"} spacing={"small"}>
            <Text variant="body">{point.name}</Text>
            <Text variant="bodySmall">{point.description || "-"}</Text>
          </Stack>
        ),
      }));
    } catch (_error) {
      return [];
    }
  };

  const { start, end } =
    parsedParams.start && parsedParams.end
      ? formatRange({ start: parsedParams.start, end: parsedParams.end })
      : { start: undefined, end: undefined };

  const comparisonYears = parsedParams.comparisonYears
    ? parsedParams.comparisonYears.filter(Boolean).sort((a, b) => a - b)
    : [];

  const { data: energyReportRaw, isLoading: energyReportLoading } =
    useEnergyReportQuery(
      {
        pointIds: pointIds.length > 0 ? pointIds : undefined,
        dataType: actualDataType,
        buildingId: buildingId,
        from: start,
        to: end,
        resolution: energyResolutionMap[parsedParams.resolution],
      },
      {
        enabled: pointIds.length > 0 || (!!buildingId && !!actualDataType),
      },
    );

  const comparisonRanges = getComparisonQueryRanges(
    comparisonYears,
    start,
    end,
  );

  const comparisonQueries = useEnergyReportQueries(
    comparisonRanges.map(
      (range): Omit<EnergyReportParams, "companyId"> => ({
        pointIds: pointIds.length > 0 ? pointIds : undefined,
        dataType: actualDataType,
        buildingId: buildingId,
        from: range.start,
        to: range.end,
        resolution: energyResolutionMap[parsedParams.resolution],
      }),
    ),
    {
      enabled:
        (pointIds.length > 0 || !!buildingId) &&
        !!start &&
        !!end &&
        !!comparisonRanges.length,
    },
  );

  const selectedBuilding = buildingId
    ? allBuildings?.find((b) => b.id === buildingId)
    : undefined;

  // Map reports according to if "Show points" is checked or not
  const showPoints =
    pointIds.length !== 1 && parsedParams.showPoints === "true";

  const selectedReports = getReportData({
    energyReport: energyReportRaw,
    dataType,
    start: parsedParams.start,
    end: parsedParams.end,
    resolution: parsedParams.resolution,
    location: selectedBuilding?.location,
  });

  const comparisonReports = comparisonQueries.map((query) =>
    getReportData({
      energyReport: query.data,
      dataType,
      start: parsedParams.start,
      end: parsedParams.end,
      resolution: parsedParams.resolution,
      location: selectedBuilding?.location,
    }),
  );

  const isLoadingComparison = comparisonQueries.some(
    (query) => query.isLoading,
  );

  const handleDataTypeSelect = (value: string) => {
    setSearchParams(
      (params) => {
        params.set("dataType", value);
        params.delete("pointIds");
        params.delete("search");
        params.delete("reportId");
        params.delete("reportName");
      },
      { replace: true },
    );
  };

  const handleBuildingSelect = (value: string) => {
    setSearchParams(
      (params) => {
        params.set("buildingId", value);
        params.delete("pointIds");
        params.delete("search");
        params.delete("reportId");
        params.delete("reportName");
      },
      { replace: true },
    );
  };

  const handlePointSelect = (selected: Set<string>) => {
    const selectedPointIds = Array.from(selected);

    setSearchParams(
      (params) => {
        params.delete("pointIds");
        params.delete("search");
        params.delete("reportId");
        params.delete("reportName");
        selectedPointIds.forEach((id) => {
          params.append("pointIds", id);
        });
      },
      { replace: true },
    );
  };

  const handleClearAll = () => {
    clearSearchParams();
  };

  const handleChangeActiveTab = (value: string) => {
    setSearchParams((params) => params.set("active", value), { replace: true });
  };

  const handleChangeComparisonYears = (years?: Set<string>) => {
    const cYears = Array.from(years ?? []);

    setSearchParams(
      (params) => {
        params.delete("comparisonYears");
        params.delete("reportId");
        params.delete("reportName");
        cYears.forEach((c) => params.append("comparisonYears", c));
      },
      { replace: true },
    );
  };
  const onApply = (value: DateRange, resolutionValue: DateResolution) => {
    setSearchParams(
      (params) => {
        params.set("resolution", resolutionValue);
        params.set("start", value.start);
        params.set("end", value.end);
        params.delete("comparisonYears");
        params.delete("reportId");
        params.delete("reportName");
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (
      !!energyReportRaw?.length &&
      !energyReportLoading &&
      !parsedParams.start &&
      !parsedParams.end
    ) {
      setSearchParams(
        (params) => {
          params.set("start", defaultDateRange.start);
          params.set("end", defaultDateRange.end);
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energyReportRaw, energyReportLoading]);

  const isLoading = energyReportLoading || isLoadingComparison;

  const placeholderText = () => {
    if (!dataType || (!buildingId && pointIds.length === 0)) {
      return t`Start by selecting a report OR type, building/points and time frame`;
    }

    if (
      !selectedReports ||
      selectedReports.length === 0 ||
      selectedReports.every((r) => r.values.length === 0)
    ) {
      return t`No data available for selected points`;
    }

    const error =
      selectedReports?.find((v) => v.error)?.error ||
      comparisonReports?.flatMap((rep) => rep ?? []).find((v) => v.error)
        ?.error;

    return error;
  };

  const value: PointEnergyReportStateContextType = {
    handleClearAll,
    handleBuildingSelect,
    handleDataTypeSelect,
    handlePointSelect,
    handleChangeActiveTab,
    handleChangeComparisonYears,
    onApply,
    comparisonReports,
    comparisonYears,
    selectedPoints,
    loadPointOptions,
    allBuildings,
    selectedReports,
    setSearchParams,
    dataType,
    buildingId,
    pointIds,
    showPoints,
    resolution: parsedParams.resolution,
    startDate: parsedParams.start,
    endDate: parsedParams.end,
    activeTab: parsedParams.active,
    search: parsedParams.search,
    defaultDateRange,
    isLoading,
    placeholderText: placeholderText(),
    endYear: DateTime.fromISO(parsedParams.end).year,
  };

  return (
    <PointEnergyReportStateContext.Provider value={value}>
      {children}
    </PointEnergyReportStateContext.Provider>
  );
}

export const usePointEnergyReportStateContext = () => {
  const ctx = useContext(PointEnergyReportStateContext);
  if (!ctx) {
    throw new Error(
      "usePointReportStateContext must be used inside PointReportStateProvider",
    );
  }
  return ctx;
};
